import { Link, useLocation } from "react-router-dom";
import Top from "./Top";
import { useEffect, useRef, useState } from "react";
import { server } from "../../server";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import GLightbox from "glightbox";
import imageCompression from "browser-image-compression";
import { toast } from "react-toastify";
import { getUserFromToken } from "../../function";

const FOLDERS_API =
  "https://b2i4nr8bza.execute-api.ap-south-1.amazonaws.com/Prod/Folders";
const BIN_API =
  "https://b2i4nr8bza.execute-api.ap-south-1.amazonaws.com/Prod/Bin";

const GOOGLE_API_KEY = "AIzaSyAWTa9J83qmXUwxKBwKG6WxsNU9h5zsl80";
const GOOGLE_CLIENT_ID =
  "990590719478-i10hb620mj2ricn42u79nrf9t8g2dn83.apps.googleusercontent.com";
const GOOGLE_PICKER_SCOPE = "https://www.googleapis.com/auth/drive.readonly";
const GOOGLE_DRIVE_CONFIGURED = Boolean(GOOGLE_API_KEY && GOOGLE_CLIENT_ID);

function Event() {
  const location = useLocation();
  const { event } = location.state || {};
  const user = getUserFromToken();
  const token = localStorage.getItem("authToken");

  // ── View: "event" = event-level images, "folder" = inside a folder ───────
  const [view, setView] = useState("event");
  const [activeFolder, setActiveFolder] = useState(null);

  // ── Folders ───────────────────────────────────────────────────────────────
  const [folders, setFolders] = useState([]);
  const [foldersLoading, setFoldersLoading] = useState(true);
  const [folderToDelete, setFolderToDelete] = useState(null);
  const [folderDeleteLoading, setFolderDeleteLoading] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createDesc, setCreateDesc] = useState("");
  const [creating, setCreating] = useState(false);

  // ── Event-level images ────────────────────────────────────────────────────
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit] = useState(300);
  const [totalImages, setTotalImages] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // ── Folder-level images ───────────────────────────────────────────────────
  const [folderImages, setFolderImages] = useState([]);
  const [folderImagesLoading, setFolderImagesLoading] = useState(false);
  const [folderPage, setFolderPage] = useState(1);
  const [folderTotalPages, setFolderTotalPages] = useState(1);
  const [folderTotalImages, setFolderTotalImages] = useState(0);
  const FOLDER_LIMIT = 20;

  // ── Shared image interaction state ────────────────────────────────────────
  const [selectedImageId, setSelectedImageId] = useState(null);
  const [trashLoading, setTrashLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false); // ← NEW
  const [selectedImages, setSelectedImages] = useState([]);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [loadedImages, setLoadedImages] = useState({});
  const [searchTerm, setSearchTerm] = useState("");

  // ── Move / remove from folder ────────────────────────────────────────────
  const [moveTargetIds, setMoveTargetIds] = useState([]);
  const [movingFolderId, setMovingFolderId] = useState(null);

  // ── Upload state ──────────────────────────────────────────────────────────
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState([]);
  const [totalProgress, setTotalProgress] = useState(0);
  const [processingFiles, setProcessingFiles] = useState(false);
  const [limitError, setLimitError] = useState("");
  const [uploadTab, setUploadTab] = useState("local");
  const [urlInput, setUrlInput] = useState("");
  const [urlLoading, setUrlLoading] = useState(false);
  const [driveLoading, setDriveLoading] = useState(false);
  const [gapiReady, setGapiReady] = useState(false);
  const [gisReady, setGisReady] = useState(false);
  const [oauthToken, setOauthToken] = useState(null);
  const tokenClientRef = useRef(null);
  const currentBatchRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const MAX_FILES = 10000;

  // ── Google APIs ───────────────────────────────────────────────────────────
  useEffect(() => {
    // Don't attempt to load Google scripts / init the token client if the
    // API key or OAuth client ID aren't configured (e.g. missing env vars in
    // this deployment). The Drive tab UI disables itself based on
    // GOOGLE_DRIVE_CONFIGURED, so this just avoids noisy runtime errors.
    if (!GOOGLE_DRIVE_CONFIGURED) {
      console.error(
        "Google Drive import disabled: missing REACT_APP_GOOGLE_API_KEY or REACT_APP_GOOGLE_CLIENT_ID environment variable.",
      );
      return;
    }

    if (!window.gapi) {
      const s = document.createElement("script");
      s.src = "https://apis.google.com/js/api.js";
      s.onload = () => window.gapi.load("picker", () => setGapiReady(true));
      document.body.appendChild(s);
    } else {
      window.gapi.load("picker", () => setGapiReady(true));
    }
    if (!window.google?.accounts) {
      const s = document.createElement("script");
      s.src = "https://accounts.google.com/gsi/client";
      s.onload = () => {
        tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
          client_id: GOOGLE_CLIENT_ID,
          scope: GOOGLE_PICKER_SCOPE,
          callback: (r) => {
            if (r.access_token) setOauthToken(r.access_token);
          },
        });
        setGisReady(true);
      };
      document.body.appendChild(s);
    } else {
      tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: GOOGLE_PICKER_SCOPE,
        callback: (r) => {
          if (r.access_token) setOauthToken(r.access_token);
        },
      });
      setGisReady(true);
    }
  }, []);
  useEffect(() => {
    if (oauthToken && gapiReady) openGooglePicker(oauthToken);
  }, [oauthToken, gapiReady]);

  // ── Fetch folders ─────────────────────────────────────────────────────────
  const fetchFolders = async () => {
    try {
      setFoldersLoading(true);
      const res = await fetch(
        `${FOLDERS_API}/GetImagesByFolder?action=getFolders&eventId=${event?.eventId}&userObjectId=${user?._id}`,
        { headers: { Authorization: token } },
      );
      const data = await res.json();
      setFolders(data?.folders || []);
    } catch (err) {
      console.error(err);
    } finally {
      setFoldersLoading(false);
    }
  };

  useEffect(() => {
    if (event?.eventId) fetchFolders();
  }, [event?.eventId]);

  // ── Fetch event-level images ──────────────────────────────────────────────
  const fetchImages = async (pageNum = page) => {
    try {
      setLoading(true);
      const res = await fetch(
        `${server}/Images/GetImagesByEventId?eventId=${event?.eventId}&page=${pageNum}&limit=${limit}`,
        {
          method: "GET",
          headers: { "Content-Type": "application/json", Authorization: token },
        },
      );
      const data = await res.json();
      if (data?.images) {
        setImages(data.images);
        setTotalImages(data.pagination.total);
        setTotalPages(data.pagination.totalPages);
      } else {
        setImages([]);
      }
      setPage(pageNum);
    } catch (err) {
      console.error(err);
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchImages(page);
  }, [page]);

  // ── Fetch folder images ───────────────────────────────────────────────────
  const fetchFolderImages = async (folderId, pg = 1) => {
    try {
      setFolderImagesLoading(true);
      const res = await fetch(
        `${FOLDERS_API}/GetImagesByFolder?action=getImages&folderId=${folderId}&page=${pg}&limit=${FOLDER_LIMIT}`,
        { headers: { Authorization: token } },
      );
      const data = await res.json();
      setFolderImages(data?.images || []);
      setFolderTotalImages(data?.pagination?.total || 0);
      setFolderTotalPages(data?.pagination?.totalPages || 1);
      setFolderPage(pg);
    } catch (err) {
      console.error(err);
    } finally {
      setFolderImagesLoading(false);
    }
  };

  const openFolder = (folder) => {
    setActiveFolder(folder);
    setView("folder");
    setSelectedImages([]);
    setIsSelectMode(false);
    setLoadedImages({});
    setSearchTerm("");
    fetchFolderImages(folder._id || folder.folderId, 1);
  };

  const closeFolder = () => {
    setView("event");
    setActiveFolder(null);
    setFolderImages([]);
    setSelectedImages([]);
    setIsSelectMode(false);
    setLoadedImages({});
    setSearchTerm("");
  };

  // ── Create / delete folder ────────────────────────────────────────────────
  const handleCreateFolder = async () => {
    if (!createName.trim()) {
      toast.error("Folder name is required.", { position: "bottom-center" });
      return;
    }
    try {
      setCreating(true);
      await fetch(`${FOLDERS_API}/CreateOrDeleteFolders`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({
          action: "create",
          eventId: event?.eventId,
          userObjectId: user?._id,
          name: createName.trim(),
          description: createDesc.trim(),
        }),
      });
      setCreateName("");
      setCreateDesc("");
      document.querySelector("#createFolderModal .btn-close-modal")?.click();
      toast.success("Folder created!", {
        position: "bottom-center",
        autoClose: 2000,
      });
      fetchFolders();
    } catch (err) {
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteFolder = async () => {
    if (!folderToDelete) return;
    try {
      setFolderDeleteLoading(true);
      await fetch(`${FOLDERS_API}/CreateOrDeleteFolders`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({
          action: "delete",
          folderId: folderToDelete._id || folderToDelete.folderId,
          eventId: event?.eventId,
          userObjectId: user?._id,
        }),
      });
      document.querySelector("#deleteFolderModal .btn-close-modal")?.click();
      toast.success("Folder deleted.", {
        position: "bottom-center",
        autoClose: 2000,
      });
      setFolderToDelete(null);
      fetchFolders();
    } catch (err) {
      console.error(err);
    } finally {
      setFolderDeleteLoading(false);
    }
  };

  // ── Image helpers ─────────────────────────────────────────────────────────
  const activeImages = view === "folder" ? folderImages : images;

  const filteredImages = activeImages.filter((img) => {
    const fileName =
      img?.key
        ?.split("/")
        ?.pop()
        ?.replace(/^[^_]+_/, "") || "";
    return fileName.toLowerCase().includes(searchTerm.toLowerCase());
  });

  const toggleSelectMode = () => {
    setIsSelectMode(!isSelectMode);
    if (isSelectMode) setSelectedImages([]);
  };
  const toggleImageSelect = (id) =>
    setSelectedImages((p) =>
      p.includes(id) ? p.filter((x) => x !== id) : [...p, id],
    );
  const toggleSelectAll = () =>
    setSelectedImages(
      selectedImages.length === activeImages.length
        ? []
        : activeImages.map((i) => i._id),
    );

  // ── Shared post-action refresh ────────────────────────────────────────────
  // ← NEW helper — avoids duplicating the refresh logic in both handlers
  const refreshAfterAction = async () => {
    setSelectedImageId(null);
    setSelectedImages([]);
    setIsSelectMode(false);
    if (view === "folder") {
      fetchFolderImages(activeFolder._id || activeFolder.folderId, folderPage);
    } else {
      setLoading(true);
      await fetchImages();
      setLoading(false);
    }
  };

  // ── Move to Bin ───────────────────────────────────────────────────────────
  const handleMoveToTrash = async () => {
    setTrashLoading(true);
    try {
      const ids =
        selectedImages.length > 0 ? selectedImages : [selectedImageId];
      if (!ids.length) return;
      await fetch(`${BIN_API}/MoveToTrashOrRestore`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({
          action: "bin",
          userObjectId: user?._id,
          eventId: event?.eventId,
          imageIds: ids,
        }),
      });
      document.querySelector("#deleteModal .btn-close-modal")?.click(); // ← modal id changed
      toast.success(
        `${ids.length} photo${ids.length > 1 ? "s" : ""} moved to Bin`,
        { position: "bottom-center", autoClose: 2000 },
      );
      await refreshAfterAction();
    } catch (err) {
      console.error(err);
      toast.error("Failed to move to Bin.", { position: "bottom-center" });
    } finally {
      setTrashLoading(false);
    }
  };

  // ── Permanent Delete ──────────────────────────────────────────────────────
  // ← NEW handler
  const handleDelete = async () => {
    setDeleteLoading(true);
    try {
      const ids =
        selectedImages.length > 0 ? selectedImages : [selectedImageId];
      if (!ids.length) return;
      await fetch(`${server}/Images/DeleteImages`, {
        method: "DELETE",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({ imageIds: ids }),
      });
      document.querySelector("#deleteModal .btn-close-modal")?.click();
      toast.success(
        `${ids.length} photo${ids.length > 1 ? "s" : ""} permanently deleted`,
        { position: "bottom-center", autoClose: 2000 },
      );
      await refreshAfterAction();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete.", { position: "bottom-center" });
    } finally {
      setDeleteLoading(false);
    }
  };

  // ── Move / remove from folder ───────────────────────────────────────────
  const handleMoveImages = async (folderId) => {
    if (!moveTargetIds.length) return;
    try {
      setMovingFolderId(folderId || "remove");
      await fetch(`${FOLDERS_API}/ChangeOrRemoveFolder`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: token },
        body: JSON.stringify({
          eventId: event?.eventId,
          userObjectId: user?._id,
          ...(folderId ? { folderId } : {}),
          imageIds: moveTargetIds,
        }),
      });
      document.querySelector("#moveFolderModal .btn-close-modal")?.click();
      toast.success(folderId ? "Moved to Folder" : "Removed from Folder", {
        position: "bottom-center",
        autoClose: 2000,
      });
      setMoveTargetIds([]);
      await Promise.all([fetchFolders(), refreshAfterAction()]);
    } catch (err) {
      console.error(err);
      toast.error(
        folderId ? "Failed to move photo(s)." : "Failed to remove from folder.",
        { position: "bottom-center" },
      );
    } finally {
      setMovingFolderId(null);
    }
  };

  const handleSingleDownload = async (imgUrl, fileName = "image.jpg") => {
    try {
      const blob = await (await fetch(imgUrl, { mode: "cors" })).blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
    }
  };

  const handleMultiDownload = async () => {
    if (!selectedImages.length) return;
    const zip = new JSZip();
    const folder = zip.folder("images");
    const sel = activeImages.filter((img) => selectedImages.includes(img._id));
    for (const [i, img] of sel.entries()) {
      const blob = await (await fetch(img.imageUrl)).blob();
      folder.file(`image_${i + 1}.jpg`, blob);
    }
    saveAs(await zip.generateAsync({ type: "blob" }), "selected_images.zip");
  };

  useEffect(() => {
    const lb = GLightbox({ selector: ".glightbox-ev" });
    return () => lb.destroy();
  }, [images, folderImages, view, isSelectMode]);

  // ── Upload helpers ────────────────────────────────────────────────────────
  const formatFileSize = (b) => {
    if (b < 1024) return `${b} bytes`;
    const kb = b / 1024;
    if (kb < 1024) return `${kb.toFixed(1)} KB`;
    return `${(kb / 1024).toFixed(1)} MB`;
  };

  const processFiles = async (selectedFiles) => {
    setProcessingFiles(true);
    const processed = [];
    for (let i = 0; i < selectedFiles.length; i += 40) {
      const chunk = selectedFiles.slice(i, i + 40);
      const results = await Promise.all(
        chunk.map(
          (file) =>
            new Promise((resolve) => {
              const preview = URL.createObjectURL(file);
              const img = new Image();
              img.onload = () => {
                resolve({
                  file,
                  preview,
                  status: "pending",
                  width: img.width,
                  height: img.height,
                  size: formatFileSize(file.size),
                });
                URL.revokeObjectURL(preview);
              };
              img.onerror = () => {
                resolve({
                  file,
                  preview: null,
                  status: "pending",
                  width: null,
                  height: null,
                  size: formatFileSize(file.size),
                });
                URL.revokeObjectURL(preview);
              };
              img.src = preview;
            }),
        ),
      );
      processed.push(...results);
      await new Promise((r) => setTimeout(r, 0));
    }
    setFiles((prev) => [...prev, ...processed]);
    setProcessingFiles(false);
  };

  const addFilesWithLimit = (newFiles) => {
    if (files.length + newFiles.length > MAX_FILES) {
      toast.error(`You can upload only ${MAX_FILES} images.`, {
        position: "bottom-center",
        autoClose: 3000,
      });
      processFiles(newFiles.slice(0, MAX_FILES - files.length));
    } else {
      processFiles(newFiles);
    }
  };

  const handleGoogleDriveClick = () => {
    if (!GOOGLE_DRIVE_CONFIGURED) {
      toast.error("Google Drive import is not configured.", {
        position: "bottom-center",
      });
      return;
    }
    if (!gisReady || !gapiReady) {
      toast.error("Google APIs not loaded yet.", { position: "bottom-center" });
      return;
    }
    if (oauthToken) openGooglePicker(oauthToken);
    else tokenClientRef.current.requestAccessToken();
  };

  const openGooglePicker = (accessToken) => {
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    new window.google.picker.PickerBuilder()
      .addView(
        new window.google.picker.View(window.google.picker.ViewId.DOCS_IMAGES),
      )
      .setOAuthToken(accessToken)
      .setDeveloperKey(GOOGLE_API_KEY)
      .enableFeature(window.google.picker.Feature.MULTISELECT_ENABLED)
      .setCallback((data) => handlePickerCallback(data, accessToken))
      .build()
      .setVisible(true);

    requestAnimationFrame(() => {
      window.scrollTo(scrollX, scrollY);
      setTimeout(() => window.scrollTo(scrollX, scrollY), 50);
      setTimeout(() => window.scrollTo(scrollX, scrollY), 150);
    });
  };

  const handlePickerCallback = async (data, accessToken) => {
    if (data.action !== window.google.picker.Action.PICKED) return;
    const docs = data.docs || [];
    if (!docs.length) return;
    setDriveLoading(true);
    try {
      const fetched = await Promise.all(
        docs.map(async (doc) => {
          const res = await fetch(
            `https://www.googleapis.com/drive/v3/files/${doc.id}?alt=media`,
            { headers: { Authorization: `Bearer ${accessToken}` } },
          );
          if (!res.ok) throw new Error();
          const blob = await res.blob();
          const ext = (blob.type || "image/jpeg").split("/")[1] || "jpg";
          return new File(
            [blob],
            doc.name.includes(".") ? doc.name : `${doc.name}.${ext}`,
            { type: blob.type || "image/jpeg" },
          );
        }),
      );
      addFilesWithLimit(fetched);
      toast.success(`${fetched.length} image(s) added from Google Drive`, {
        position: "bottom-center",
        autoClose: 2500,
      });
    } catch {
      toast.error("Failed to fetch some images from Google Drive.", {
        position: "bottom-center",
      });
    } finally {
      setDriveLoading(false);
    }
  };

  const handleUrlUpload = async () => {
    const urls = urlInput
      .split("\n")
      .map((u) => u.trim())
      .filter(Boolean);
    if (!urls.length) {
      toast.error("Enter at least one URL.", { position: "bottom-center" });
      return;
    }
    setUrlLoading(true);
    const fetched = [];
    const failed = [];
    await Promise.all(
      urls.map(async (url, idx) => {
        try {
          let res;
          try {
            res = await fetch(url);
          } catch {
            res = await fetch(
              `https://corsproxy.io/?${encodeURIComponent(url)}`,
            );
          }
          if (!res.ok) throw new Error();
          const blob = await res.blob();
          if (!blob.type.startsWith("image/")) throw new Error();
          const parts = new URL(url).pathname.split("/");
          let name = parts[parts.length - 1] || `image_${idx + 1}`;
          if (!name.includes("."))
            name += `.${blob.type.split("/")[1] || "jpg"}`;
          fetched.push(new File([blob], name, { type: blob.type }));
        } catch {
          failed.push(url);
        }
      }),
    );
    if (fetched.length) {
      addFilesWithLimit(fetched);
      toast.success(`${fetched.length} image(s) added.`, {
        position: "bottom-center",
        autoClose: 2500,
      });
    }
    if (failed.length)
      toast.error(`${failed.length} URL(s) failed.`, {
        position: "bottom-center",
      });
    setUrlInput("");
    setUrlLoading(false);
  };

  const handleUpload = async () => {
    try {
      setUploading(true);
      setFiles((p) => p.map((f) => ({ ...f, status: "uploading" })));
      const batchSize = 5;
      const totalFiles = files.length;
      let keysBuffer = [];
      let stopUpload = false;
      const folderId =
        view === "folder"
          ? activeFolder?._id || activeFolder?.folderId
          : undefined;

      const uploadKeyChunk = async (chunk) => {
        const res = await fetch(`${server}/Upload/UploadImage`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: token },
          body: JSON.stringify({
            userObjectId: event.userObjectId,
            eventId: event.eventId,
            ...(folderId ? { folderId } : {}),
            keys: chunk,
          }),
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          if (
            res.status === 403 ||
            err?.error?.toLowerCase().includes("photo limit")
          ) {
            setLimitError("Photo limit reached — Please upgrade your plan");
            stopUpload = true;
            return false;
          }
        }
        return true;
      };

      for (
        let start = 0;
        start < totalFiles && !stopUpload;
        start += batchSize
      ) {
        const batch = files.slice(start, start + batchSize);
        currentBatchRef.current = start / batchSize;

        const compressed = await Promise.all(
          batch.map(async (f) => {
            const opts = {
              maxSizeMB: 1,
              maxWidthOrHeight: 1920,
              useWebWorker: true,
            };
            try {
              let blob = await imageCompression(f.file, opts);
              if (blob.size > 1024 * 1024)
                blob = await imageCompression(f.file, {
                  ...opts,
                  maxSizeMB: 0.8,
                });
              return new File([blob], f.file.name, { type: f.file.type });
            } catch {
              return f.file;
            }
          }),
        );

        const { urls } = await (
          await fetch(`${server}/PresignedUrl/GeneratePresignedUrl`, {
            method: "POST",
            headers: { Authorization: token },
            body: JSON.stringify({
              folder: "event-uploads",
              fileNames: compressed.map((f) => f.name),
            }),
          })
        ).json();

        await Promise.all(
          compressed.map((file, i) => {
            const gi = start + i;
            const ud = urls[i];
            return new Promise((resolve, reject) => {
              const xhr = new XMLHttpRequest();
              xhr.open("PUT", ud.signedUrl, true);
              xhr.setRequestHeader("Content-Type", ud.contentType);
              xhr.upload.onprogress = (e) => {
                if (e.lengthComputable) {
                  const pct = Math.round((e.loaded / e.total) * 100);
                  setUploadProgress((prev) => {
                    const u = [...prev];
                    u[gi] = pct;
                    setTotalProgress(
                      Math.round(
                        u.reduce((s, v) => s + (v || 0), 0) / totalFiles,
                      ),
                    );
                    return u;
                  });
                }
              };
              xhr.onload = () => {
                if (xhr.status === 200) {
                  setFiles((p) =>
                    p.map((f, idx) =>
                      idx === gi ? { ...f, status: "done" } : f,
                    ),
                  );
                  resolve();
                } else reject();
              };
              xhr.onerror = reject;
              xhr.send(file);
            });
          }),
        );

        keysBuffer.push(...urls.map((u) => u.imageKey));
        while (keysBuffer.length >= 100 && !stopUpload) {
          const ok = await uploadKeyChunk(keysBuffer.slice(0, 100));
          if (!ok) break;
          keysBuffer = keysBuffer.slice(100);
        }
      }

      if (!stopUpload && keysBuffer.length > 0)
        await uploadKeyChunk(keysBuffer);
      if (stopUpload) {
        setFiles((p) => p.map((f) => ({ ...f, status: "pending" })));
        setUploading(false);
        return;
      }

      document.querySelector("#imageUploadModal .btn-close-modal")?.click();
      setFiles([]);
      setUploadProgress([]);
      setTotalProgress(0);
      currentBatchRef.current = null;

      if (view === "folder")
        fetchFolderImages(
          activeFolder._id || activeFolder.folderId,
          folderPage,
        );
      else {
        setLoading(true);
        await fetchImages();
        setLoading(false);
      }
    } catch {
      setFiles((p) => p.map((f) => ({ ...f, status: "pending" })));
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (i) => setFiles((p) => p.filter((_, idx) => idx !== i));

  useEffect(() => {
    if (currentBatchRef.current !== null && scrollContainerRef.current) {
      setTimeout(() => {
        const el = scrollContainerRef.current.querySelector(
          `[data-batch="${currentBatchRef.current}"]`,
        );
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 150);
    }
  }, [files]);

  // ── Derived ───────────────────────────────────────────────────────────────
  const currentTotal = view === "folder" ? folderTotalImages : totalImages;
  const currentLoading = view === "folder" ? folderImagesLoading : loading;
  const currentTotalPages = view === "folder" ? folderTotalPages : totalPages;
  const currentPage = view === "folder" ? folderPage : page;

  const handlePageChange = (pg) => {
    if (view === "folder")
      fetchFolderImages(activeFolder._id || activeFolder.folderId, pg);
    else setPage(pg);
  };

  // Disable both action buttons while either is in flight
  const anyActionLoading = trashLoading || deleteLoading; // ← NEW

  // Folders the selected photo(s) could be moved into (excludes the folder
  // currently open, if any)
  const otherFolders = folders.filter(
    (folder) =>
      (folder._id || folder.folderId) !==
      (activeFolder?._id || activeFolder?.folderId),
  );

  return (
    <>
      <main>
        <Top />

        {/* ── Event Banner ── */}
        <div>
          <img
            src={event?.eventPhoto || "/assets/images/default.png"}
            className="w-100 object-fit-cover mb-3"
            height="300"
            alt={event?.name}
          />
        </div>

        <div className="container">
          <div className="row g-3">
            <div className="col-12 py-2">
              {/* ── Header ── */}
              <section className="position-relative z-1">
                <div className="pb-3 d-flex flex-column flex-md-row gap-0 align-items-md-center justify-content-between">
                  <div className="d-flex align-items-center">
                    {view === "folder" ? (
                      <button
                        className="action-button-md bg-white-transparent rounded-circle border-0"
                        onClick={closeFolder}
                      >
                        <i className="bi bi-arrow-left fs-4 text-dark"></i>
                      </button>
                    ) : (
                      <Link
                        to="/all-events"
                        className="action-button-md bg-white-transparent rounded-circle"
                      >
                        <i className="bi bi-arrow-left fs-4 text-dark"></i>
                      </Link>
                    )}

                    <div className="ms-3">
                      <h1 className="mb-0 h4 text-light lh-1 fw-medium">
                        {view === "folder" ? activeFolder?.name : event?.name}
                        <span className="text-light fs-6 ms-1 opacity-75">
                          ({currentTotal}{" "}
                          {currentTotal === 1 ? "Photo" : "Photos"})
                        </span>
                      </h1>
                      <nav aria-label="breadcrumb" className="m-0">
                        <ol className="breadcrumb">
                          <li className="breadcrumb-item">
                            <span>Home</span>
                          </li>
                          <li className="breadcrumb-item">
                            <span>All Events</span>
                          </li>
                          {view === "folder" ? (
                            <>
                              <li className="breadcrumb-item">
                                <span
                                  className="cursor-pointer text-light opacity-75"
                                  onClick={closeFolder}
                                >
                                  {event?.name}
                                </span>
                              </li>
                              <li
                                className="breadcrumb-item active"
                                aria-current="page"
                              >
                                {activeFolder?.name}
                              </li>
                            </>
                          ) : (
                            <li
                              className="breadcrumb-item active text-truncate"
                              aria-current="page"
                            >
                              {event?.name}
                            </li>
                          )}
                        </ol>
                      </nav>
                    </div>

                    {/* Desktop actions */}
                    {event?.type !== "shared" && (
                      <div className="d-lg-flex d-none align-items-center ms-8 gap-2">
                        <span
                          type="button"
                          data-bs-toggle="modal"
                          data-bs-target="#imageUploadModal"
                          className="btn btn-dark-success border-0 px-5 rounded-pill fs-6 fw-medium"
                        >
                          <i className="bi bi-upload me-1"></i> Upload
                        </span>
                        {!isSelectMode && (
                          <p
                            className="m-0 cursor-pointer fs-btn text-light ms-2"
                            onClick={toggleSelectMode}
                          >
                            <i className="bi bi-check2-circle me-1 text-light"></i>{" "}
                            MultiSelect
                          </p>
                        )}
                      </div>
                    )}
                  </div>

                  <div className="d-flex align-items-center gap-2 mt-lg-0 mt-3 flex-wrap">
                    {/* Mobile actions */}
                    {event?.type !== "shared" && (
                      <div className="d-lg-none d-flex align-items-center gap-2">
                        <span
                          type="button"
                          data-bs-toggle="modal"
                          data-bs-target="#imageUploadModal"
                          className="btn btn-dark-success border-0 px-4 rounded-pill fs-6 fw-medium"
                        >
                          <i className="bi bi-upload me-1"></i> Upload
                        </span>
                        {!isSelectMode && (
                          <p
                            className="m-0 cursor-pointer fs-btn text-light"
                            onClick={toggleSelectMode}
                          >
                            <i className="bi bi-check2-circle me-1 text-light"></i>{" "}
                            MultiSelect
                          </p>
                        )}
                      </div>
                    )}

                    <form className="d-flex align-items-center">
                      <div className="d-flex align-items-center">
                        <span className="position-absolute ps-3 search-icon">
                          <i className="bi bi-search ms-1"></i>
                        </span>
                        <input
                          type="text"
                          className="form-control px-7 normal-text border-0 bg-card-dark rounded-pill"
                          placeholder="Search Image"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                        />
                      </div>
                    </form>
                  </div>
                </div>
              </section>

              {/* ════════════ FOLDERS STRIP (only on event view) ════════════ */}
              {view === "event" && (
                <section className="position-relative z-1 mb-3">
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <div className="d-flex align-items-center gap-2">
                      <i className="bi bi-folder2 text-light opacity-75"></i>
                      <span className="text-light fw-medium fs-6">
                        Folders
                        {!foldersLoading && (
                          <span className="opacity-75 ms-1">
                            ({folders.length})
                          </span>
                        )}
                      </span>
                    </div>
                  </div>

                  {foldersLoading ? (
                    <div className="d-flex gap-2 py-2">
                      {[1, 2, 3].map((n) => (
                        <div
                          key={n}
                          className="rounded-4 bg-card-dark"
                          style={{ width: 160, height: 110, opacity: 0.5 }}
                        ></div>
                      ))}
                    </div>
                  ) : folders.length === 0 ? (
                    <div
                      className="d-flex align-items-center gap-3 rounded-4 px-5 py-3 bg-card-dark cursor-pointer"
                      style={{
                        border: "1.5px dashed rgba(255,255,255,0.15)",
                        width: "fit-content",
                      }}
                      data-bs-toggle={
                        event?.type !== "shared" ? "modal" : undefined
                      }
                      data-bs-target={
                        event?.type !== "shared"
                          ? "#createFolderModal"
                          : undefined
                      }
                    >
                      <i className="bi bi-folder-plus text-light opacity-50 fs-2"></i>
                      <div>
                        <p className="text-light m-0 lh-1 fw-medium fs-6">
                          No folders yet
                        </p>
                        {event?.type !== "shared" && (
                          <p className="text-light opacity-50 m-0 smtext me-5">
                            Click to create one
                          </p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div
                      className="d-flex gap-2 overflow-auto pb-2"
                      style={{ scrollbarWidth: "none" }}
                    >
                      {folders.map((folder) => (
                        <div
                          key={folder._id || folder.folderId}
                          className="flex-shrink-0 position-relative folder-card-strip"
                          style={{ width: 160 }}
                        >
                          {/* Folder card */}
                          <div
                            className="rounded-4 overflow-hidden bg-white cursor-pointer"
                            onClick={() => openFolder(folder)}
                            style={{ height: 110 }}
                          >
                            {folder.thumbnailUrl ? (
                              <img
                                src={folder.thumbnailUrl}
                                className="w-100 h-100 object-fit-cover"
                                alt={folder.name}
                              />
                            ) : (
                              <div className="w-100 h-100 d-flex align-items-center justify-content-center bg-card-dark">
                                <i
                                  className="bi bi-folder-fill text-primary"
                                  style={{ fontSize: 40, opacity: 0.3 }}
                                ></i>
                              </div>
                            )}
                          </div>

                          {/* Label row */}
                          <div className="d-flex align-items-center justify-content-between mt-1 px-1">
                            <div
                              className="overflow-hidden"
                              style={{ maxWidth: "calc(100% - 28px)" }}
                            >
                              <p
                                className="text-light fw-medium m-0 lh-1 mt-2 text-truncate cursor-pointer"
                                style={{ fontSize: 13 }}
                                onClick={() => openFolder(folder)}
                              >
                                {folder.name}
                              </p>
                              <p
                                className="text-light opacity-50 m-0"
                                style={{ fontSize: 11 }}
                              >
                                {(folder.imageCount || 0).toLocaleString(
                                  "en-IN",
                                )}{" "}
                                photos
                              </p>
                            </div>
                            {event?.type !== "shared" && (
                              <span
                                role="button"
                                className="text-light opacity-50 ms-1 flex-shrink-0"
                                style={{ fontSize: 13 }}
                                data-bs-toggle="modal"
                                data-bs-target="#deleteFolderModal"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setFolderToDelete(folder);
                                }}
                              >
                                <i className="bi bi-trash2"></i>
                              </span>
                            )}
                          </div>
                        </div>
                      ))}

                      {/* Add new folder tile */}
                      {event?.type !== "shared" && (
                        <div
                          className="flex-shrink-0 rounded-4 d-flex align-items-center justify-content-center cursor-pointer bg-card-dark"
                          style={{
                            width: 160,
                            height: 110,
                            border: "1.5px dashed rgba(255,255,255,0.15)",
                          }}
                          data-bs-toggle="modal"
                          data-bs-target="#createFolderModal"
                        >
                          <div className="text-center">
                            <i className="bi bi-folder-plus text-light opacity-40 fs-2"></i>
                            <p
                              className="text-light opacity-50 m-0 mt-1"
                              style={{ fontSize: 12 }}
                            >
                              New Folder
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Divider */}
                  <div className="mt-4 mb-1 d-flex align-items-center gap-2">
                    <i className="bi bi-images text-light opacity-75"></i>
                    <span className="text-light fw-medium fs-6">
                      All Photos
                      {!loading && (
                        <span className="opacity-75 ms-1">({totalImages})</span>
                      )}
                    </span>
                  </div>
                </section>
              )}

              {/* ════════════ IMAGE GRID ════════════ */}
              <div
                className={`mt-1 ${isSelectMode ? "mb-8 pb-8" : "mb-8 pb-5"}`}
              >
                <div className="row g-2 mb-5">
                  {currentLoading ? (
                    <div className="d-flex justify-content-center align-items-center py-5">
                      <div
                        className="spinner-border text-light"
                        role="status"
                      ></div>
                    </div>
                  ) : filteredImages.length === 0 ? (
                    <div className="pb-5">
                      <img
                        src="/assets/images/no-gallary.png"
                        width="300"
                        alt="No images"
                      />
                    </div>
                  ) : (
                    filteredImages.map((img, index) => (
                      <div
                        key={img._id || index}
                        className="position-relative col-lg-2 col-md-4 col-6"
                      >
                        <div
                          className="position-relative"
                          onClick={() => {
                            if (isSelectMode) toggleImageSelect(img._id);
                          }}
                          style={{
                            cursor: isSelectMode ? "pointer" : "default",
                          }}
                        >
                          {isSelectMode ? (
                            <img
                              src={img.thumbnailUrl}
                              data-full={img.thumbnailUrl}
                              className="w-100 rounded-4 object-fit-cover"
                              height="170"
                              loading="lazy"
                              draggable={false}
                              style={{
                                opacity: loadedImages[img._id] ? 1 : 0,
                                transition: "opacity 0.3s ease",
                                filter: selectedImages.includes(img._id)
                                  ? "brightness(0.6)"
                                  : "none",
                              }}
                              onLoad={(e) => {
                                const full = new Image();
                                full.src = e.target.dataset.full;
                                full.onload = () => {
                                  e.target.src = full.src;
                                  setLoadedImages((p) => ({
                                    ...p,
                                    [img._id]: true,
                                  }));
                                };
                              }}
                              alt="photo"
                            />
                          ) : (
                            <a href={img.imageUrl} className="glightbox-ev">
                              <img
                                src={img.thumbnailUrl}
                                data-full={img.thumbnailUrl}
                                className="w-100 rounded-4 object-fit-cover"
                                height="170"
                                loading="lazy"
                                draggable={false}
                                style={{
                                  opacity: loadedImages[img._id] ? 1 : 0,
                                  transition: "opacity 0.3s ease",
                                }}
                                onLoad={(e) => {
                                  const full = new Image();
                                  full.src = e.target.dataset.full;
                                  full.onload = () => {
                                    e.target.src = full.src;
                                    setLoadedImages((p) => ({
                                      ...p,
                                      [img._id]: true,
                                    }));
                                  };
                                }}
                                alt="photo"
                              />
                            </a>
                          )}

                          {isSelectMode && selectedImages.includes(img._id) && (
                            <div
                              className="position-absolute top-50 start-50 translate-middle z-2"
                              style={{ pointerEvents: "none" }}
                            >
                              <i
                                className="bi bi-check-circle-fill text-white"
                                style={{
                                  fontSize: 34,
                                  filter:
                                    "drop-shadow(0 1px 3px rgba(0,0,0,0.5))",
                                }}
                              ></i>
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Multi-select bar */}
              {isSelectMode && (
                <div className="fixed-bottom container w-lg-50 w-100 mt-7 mb-8 pb-3 d-flex">
                  <div className="bg-white py-2 ps-5 rounded-4 col-12 overflow-hidden">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="d-flex align-items-center">
                        <span
                          onClick={toggleSelectMode}
                          className="fs-4 rounded-3 me-3 cursor-pointer"
                        >
                          <i className="bi bi-x-lg"></i>
                        </span>
                        <h5 className="m-0 fw-normal">
                          Selected ({selectedImages.length}/
                          {activeImages.length})
                        </h5>
                      </div>
                      <div className="d-flex align-items-center">
                        <span
                          className="fs-6 rounded-3 me-1 circle-md-btn rounded-circle cursor-pointer btn btn-secondary"
                          onClick={
                            selectedImages.length > 0
                              ? handleMultiDownload
                              : undefined
                          }
                        >
                          <i className="bi bi-download text-white"></i>
                        </span>
                        {event?.type !== "shared" && (
                          <span
                            className={`fs-6 rounded-3 me-1 circle-md-btn rounded-circle cursor-pointer btn btn-dark ${selectedImages.length === 0 ? "pe-none" : ""}`}
                            data-bs-toggle={
                              selectedImages.length > 0 ? "modal" : undefined
                            }
                            data-bs-target={
                              selectedImages.length > 0
                                ? "#moveFolderModal"
                                : undefined
                            }
                            onClick={() =>
                              selectedImages.length > 0 &&
                              setMoveTargetIds(selectedImages)
                            }
                          >
                            <i className="bi bi-folder-symlink text-white"></i>
                          </span>
                        )}
                        {/* ← was #trashModal */}
                        <span
                          className={`fs-6 rounded-3 me-1 circle-md-btn rounded-circle cursor-pointer btn btn-success ${selectedImages.length === 0 ? "pe-none" : ""}`}
                          data-bs-toggle={
                            selectedImages.length > 0 ? "modal" : undefined
                          }
                          data-bs-target={
                            selectedImages.length > 0
                              ? "#deleteModal"
                              : undefined
                          }
                        >
                          <i className="bi bi-trash2 text-white"></i>
                        </span>
                        <span
                          onClick={toggleSelectAll}
                          className="btn btn-primary ps-4 pe-7 fs-6 rounded-start-pill"
                        >
                          {selectedImages.length === activeImages.length
                            ? "De-Select"
                            : "Select"}{" "}
                          All
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Pagination */}
            {!currentLoading && currentTotalPages > 1 && (
              <div className="d-flex justify-content-center fixed-bottom">
                <nav>
                  <ul className="pagination">
                    <li
                      className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                    >
                      <button
                        className="page-link"
                        onClick={() => handlePageChange(currentPage - 1)}
                      >
                        <i className="bi bi-chevron-left"></i>
                      </button>
                    </li>
                    {[...Array(currentTotalPages)]
                      .slice(
                        Math.max(0, currentPage - 3),
                        Math.min(currentTotalPages, currentPage + 2),
                      )
                      .map((_, i) => {
                        const n = Math.max(1, currentPage - 2) + i;
                        return (
                          <li
                            key={n}
                            className={`page-item ${currentPage === n ? "active" : ""}`}
                          >
                            <button
                              className="page-link"
                              onClick={() => handlePageChange(n)}
                            >
                              {n}
                            </button>
                          </li>
                        );
                      })}
                    <li
                      className={`page-item ${currentPage === currentTotalPages ? "disabled" : ""}`}
                    >
                      <button
                        className="page-link me-0"
                        onClick={() => handlePageChange(currentPage + 1)}
                      >
                        <i className="bi bi-chevron-right"></i>
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* ════════ CREATE FOLDER MODAL ════════ */}
      <div
        className="modal fade"
        id="createFolderModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-md modal-dialog-centered">
          <div className="modal-content rounded-4">
            <div className="modal-body py-0 ps-5">
              <div className="d-flex justify-content-end mt-n8">
                <img src="/assets/images/rocket.svg" width="130" alt="" />
              </div>
              <div className="mt-n7">
                <h4 className="ls-sm mb-3">
                  <i className="bi bi-folder-plus"></i> &nbsp; New Folder
                </h4>
                <div className="mb-3">
                  <label className="label-text mb-1 ms-1">
                    Folder Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control border border-light rounded-3 normal-text"
                    placeholder="e.g. Stage Ceremony"
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
                  />
                </div>
                <div className="mb-3">
                  <label className="label-text mb-1 ms-1">
                    Description{" "}
                    <span className="text-secondary fw-normal">(optional)</span>
                  </label>
                  <textarea
                    className="form-control border border-light rounded-3 normal-text"
                    placeholder="Short description…"
                    value={createDesc}
                    onChange={(e) => setCreateDesc(e.target.value)}
                    rows={4}
                  />
                </div>
                <button
                  onClick={handleCreateFolder}
                  disabled={creating}
                  className="btn btn-dark-primary border-0 px-5 rounded-3 fs-6 fw-medium mt-1"
                >
                  {creating ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                    ></span>
                  ) : (
                    <>
                      <i className="bi bi-folder-plus me-1"></i> Create Folder
                    </>
                  )}
                </button>
              </div>
            </div>
            <div className="modal-footer border-0 p-0">
              <button
                type="button"
                className="btn btn-success rounded-circle px-3 label-text text-white btn-close-modal me-n3 mb-n2"
                data-bs-dismiss="modal"
                onClick={() => {
                  setCreateName("");
                  setCreateDesc("");
                }}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ════════ DELETE FOLDER MODAL ════════ */}
      <div
        className="modal fade"
        id="deleteFolderModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-md modal-dialog-centered">
          <div className="modal-content rounded-4">
            <div className="modal-body py-0 ps-5">
              <div className="d-flex justify-content-end mt-n8">
                <img src="/assets/images/trash.png" width="140" alt="" />
              </div>
              <div className="mt-n7">
                <h4 className="ls-sm mb-3">
                  <i className="bi bi-trash"></i> &nbsp; Delete Folder
                </h4>
                <p className="fs-5 lh-sm text-dark mb-1">
                  Delete <strong>{folderToDelete?.name}</strong>?
                </p>
                <p className="fs-6 lh-sm mt-2">
                  All images inside will be{" "}
                  <span className="fw-medium text-danger">
                    permanently deleted
                  </span>{" "}
                  and cannot be recovered.
                </p>
                <button
                  onClick={handleDeleteFolder}
                  disabled={folderDeleteLoading}
                  className="btn btn-dark-primary border-0 px-4 rounded-3 fs-6 fw-medium mt-3 mb-4"
                >
                  {folderDeleteLoading ? (
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                    ></span>
                  ) : (
                    "Delete"
                  )}
                </button>
              </div>
            </div>
            <div className="modal-footer border-0 p-0">
              <button
                type="button"
                className="btn btn-success rounded-circle px-3 label-text text-white btn-close-modal me-n3 mb-n2"
                data-bs-dismiss="modal"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ════════ MOVE TO FOLDER MODAL ════════ */}
      <div
        className="modal fade"
        id="moveFolderModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-md modal-dialog-centered">
          <div className="modal-content rounded-4">
            <div className="modal-body py-4 px-5">
              <h4 className="ls-sm mb-0 lh-sm">
                <i className="bi bi-folder-symlink"></i> &nbsp; Move to Folder
              </h4>
              <p className="fs-6 text-secondary lh-sm mb-3">
                {moveTargetIds.length} photo
                {moveTargetIds.length > 1 ? "s" : ""} selected. Choose a folder
                below.
              </p>

              <div
                className="d-flex flex-wrap gap-1 pb-1 mt-3"
                style={{ maxHeight: 320, overflowY: "auto" }}
              >
                {view === "folder" && (
                  <div
                    className="d-flex flex-column align-items-center cursor-pointer position-relative"
                    style={{ width: 90 }}
                    onClick={() => handleMoveImages(undefined)}
                  >
                    <div
                      className="rounded-4 d-flex align-items-center justify-content-center w-100"
                      style={{ height: 70, background: "rgba(220,53,69,0.08)" }}
                    >
                      <i
                        className="bi bi-folder-x text-danger"
                        style={{ fontSize: 28 }}
                      ></i>
                    </div>
                    <p
                      className="text-dark m-0 mt-1 text-center text-truncate w-100"
                      style={{ fontSize: 12 }}
                    >
                      Remove from Folder
                    </p>
                    {movingFolderId === "remove" && (
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded-4"
                        style={{ background: "rgba(255,255,255,0.75)" }}
                      >
                        <span
                          className="spinner-border spinner-border-sm text-danger"
                          role="status"
                        ></span>
                      </div>
                    )}
                  </div>
                )}

                {otherFolders.map((folder) => {
                  const fId = folder._id || folder.folderId;
                  return (
                    <div
                      key={fId}
                      className="d-flex flex-column align-items-center cursor-pointer position-relative"
                      style={{ width: 90 }}
                      onClick={() => handleMoveImages(fId)}
                    >
                      <div
                        className="rounded-4 overflow-hidden bg-dark-soft w-100"
                        style={{ height: 70 }}
                      >
                        {folder.thumbnailUrl ? (
                          <img
                            src={folder.thumbnailUrl}
                            className="w-100 h-100 object-fit-cover"
                            alt={folder.name}
                          />
                        ) : (
                          <div className="w-100 h-100 d-flex align-items-center justify-content-center">
                            <i
                              className="bi bi-folder-fill text-primary"
                              style={{ fontSize: 28, opacity: 0.5 }}
                            ></i>
                          </div>
                        )}
                      </div>
                      <p
                        className="text-dark m-0 mt-1 text-center text-truncate w-100"
                        style={{ fontSize: 12 }}
                      >
                        {folder.name}
                      </p>
                      {movingFolderId === fId && (
                        <div
                          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center rounded-4"
                          style={{ background: "rgba(255,255,255,0.75)" }}
                        >
                          <span
                            className="spinner-border spinner-border-sm text-danger"
                            role="status"
                          ></span>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>

              {otherFolders.length === 0 && (
                <p className="text-secondary fs-6 text-center py-3 mb-0">
                  No other folders yet. Create one first.
                </p>
              )}
            </div>
            <div className="modal-footer border-0 p-0">
              <button
                type="button"
                className="btn btn-success rounded-circle px-3 label-text text-white btn-close-modal me-n3 mb-n2"
                data-bs-dismiss="modal"
                onClick={() => setMoveTargetIds([])}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ════════ REMOVE IMAGE MODAL — Move to Bin  /  Delete Forever ════════ */}
      {/*
        Replaces the old #trashModal with a single #deleteModal that presents

        both options side-by-side so the user always makes an explicit choice.
      */}
      <div
        className="modal fade"
        id="deleteModal"
        tabIndex="-1"
        aria-labelledby="deleteModalLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content rounded-4">
            <div className="modal-body py-0 ps-5">
              <div className="d-flex justify-content-end mt-n8">
                <img
                  src="/assets/images/trash.png"
                  width="140"
                  alt="Trash Icon"
                />
              </div>
              <div className="mt-n7">
                <h4 className="ls-sm lh-sm mb-0" id="deleteModalLabel">
                  <i className="bi bi-trash3"></i> &nbsp;
                  {selectedImages.length > 1 ? "Remove Photos" : "Remove Photo"}
                </h4>
                <p className="fs-6 text-secondary lh-sm mb-4">
                  {selectedImages.length > 1
                    ? `Choose what to do with ${selectedImages.length} selected photos.`
                    : "Choose what to do with this photo."}
                </p>

                <div className="row g-2">
                  <div className="col-lg-6 col-12">
                    <div
                      className="rounded-4 px-4 pt-3 mb-1 h-100"
                      style={{
                        border: "1.5px solid rgba(0,0,0,0.09)",
                        background: "rgba(248,249,250,1)",
                      }}
                    >
                      <div className="d-flex align-items-start gap-3">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0 mt-1"
                          style={{
                            width: 36,
                            height: 36,
                            background: "rgba(13,110,253,0.08)",
                          }}
                        >
                          <i
                            className="bi bi-trash3 text-primary"
                            style={{ fontSize: 15 }}
                          ></i>
                        </div>
                        <div>
                          <p className="fw-medium text-dark m-0 lh-1 mb-1">
                            Move to Bin
                          </p>
                          <p className="text-secondary m-0 fs-6 lh-sm mt-2">
                            Photos stay recoverable. Guests won't see the photos
                            in bin.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleMoveToTrash}
                        disabled={anyActionLoading}
                        className="btn btn-dark-primary border-0 px-4 rounded-3 fs-6 fw-medium mt-3 w-100"
                      >
                        {trashLoading ? (
                          <div
                            className="spinner-border spinner-border-sm text-light"
                            role="status"
                          ></div>
                        ) : (
                          <>
                            <i className="bi bi-trash3 me-1"></i> Move to Bin
                          </>
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="col-lg-6 col-12">
                    <div
                      className="rounded-4 px-4 pt-3 mb-4 h-100"
                      style={{
                        border: "1.5px solid rgba(220,53,69,0.15)",
                        background: "rgba(220,53,69,0.04)",
                      }}
                    >
                      <div className="d-flex align-items-start gap-3">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0 mt-1"
                          style={{
                            width: 36,
                            height: 36,
                            background: "rgba(220,53,69,0.10)",
                          }}
                        >
                          <i
                            className="bi bi-slash-circle text-danger"
                            style={{ fontSize: 15 }}
                          ></i>
                        </div>
                        <div>
                          <p className="fw-medium text-dark m-0 lh-1 mb-1">
                            Delete Forever
                          </p>
                          <p className="text-secondary m-0 fs-6 lh-sm mt-2">
                            Permanently removes photos.{" "}
                            <span className="text-danger fw-medium">
                              Cannot be undone.
                            </span>
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={handleDelete}
                        disabled={anyActionLoading}
                        className="btn btn-danger border-0 px-4 rounded-3 fs-6 fw-medium mt-3 w-100"
                      >
                        {deleteLoading ? (
                          <div
                            className="spinner-border spinner-border-sm text-light"
                            role="status"
                          ></div>
                        ) : (
                          <>
                            <i className="bi bi-slash-circle me-1"></i> Delete
                            Forever
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer border-0 p-0">
              <button
                type="button"
                className="btn btn-success rounded-circle px-3 label-text text-white btn-close-modal me-n3 mb-n2"
                data-bs-dismiss="modal"
                onClick={() => setSelectedImageId(null)}
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ════════ UPLOAD MODAL ════════ */}
      <div
        className="modal fade"
        id="imageUploadModal"
        tabIndex="-1"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-xl modal-dialog-centered">
          <div className="modal-content rounded-4">
            <div className="modal-body">
              <div className="position-relative">
                <button
                  type="button"
                  className="btn btn-success rounded-circle px-3 label-text text-white btn-close-modal me-n3 mb-n2 position-absolute"
                  data-bs-dismiss="modal"
                >
                  <i className="bi bi-x-lg"></i>
                </button>
              </div>

              <div className="row py-1">
                {/* LEFT */}
                <div className="col-lg-6 col-12 d-flex justify-content-center align-items-center">
                  <div className="ms-4 me-2 w-100">
                    <h4 className="mb-0 normal-text lh-1 text-center">
                      <span className="text-dark ls-sm fs-4">
                        {view === "folder"
                          ? `Upload to "${activeFolder?.name}"`
                          : "Upload Photos — Quick & Easy"}
                      </span>
                    </h4>
                    <p className="m-0 fs-5 ls-sm text-center text-dark opacity-75 lh-1 mt-2">
                      {view === "folder"
                        ? "Photos will be added directly to this folder."
                        : "Upload photos to instantly add them to your event gallery."}
                    </p>

                    <div className="d-flex justify-content-center gap-1 mt-2 mb-4 pt-2">
                      {["local", "drive", "url"].map((tab) => (
                        <button
                          key={tab}
                          className={`btn btn-sm rounded-3 px-3 ${uploadTab === tab ? "btn-dark" : "btn-outline-secondary"}`}
                          onClick={() => setUploadTab(tab)}
                        >
                          {tab === "local" && (
                            <>
                              <i className="bi bi-folder me-1"></i>Device
                            </>
                          )}
                          {tab === "drive" && (
                            <>
                              <svg
                                xmlns="http://www.w3.org/2000/svg"
                                width="13"
                                height="13"
                                viewBox="0 0 87.3 78"
                                className="me-1"
                              >
                                <path
                                  d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3L28 48.2H0c0 1.55.4 3.1 1.2 4.5z"
                                  fill="#0066da"
                                />
                                <path
                                  d="M43.65 24.7L29.35 0c-1.35.8-2.5 1.9-3.3 3.3L1.2 43.7c-.8 1.4-1.2 2.95-1.2 4.5h28z"
                                  fill="#00ac47"
                                />
                                <path
                                  d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75L86.1 57.2c.8-1.4 1.2-2.95 1.2-4.5H59.3l5.9 11.5z"
                                  fill="#ea4335"
                                />
                                <path
                                  d="M43.65 24.7l14.3-24.7c-1.35-.8-2.9-1.2-4.5-1.2H33.85c-1.6 0-3.15.45-4.5 1.2z"
                                  fill="#00832d"
                                />
                                <path
                                  d="M59.3 48.2H28L13.75 76.8c1.35.8 2.9 1.2 4.5 1.2h50c1.6 0 3.15-.45 4.5-1.2z"
                                  fill="#2684fc"
                                />
                                <path
                                  d="M73.4 26.5l-13.1-22.7c-.8-1.4-1.95-2.5-3.3-3.3L43.65 24.7l15.65 23.5h27.9c0-1.55-.4-3.1-1.2-4.5z"
                                  fill="#ffba00"
                                />
                              </svg>
                              Drive
                            </>
                          )}
                          {tab === "url" && (
                            <>
                              <i className="bi bi-link-45deg me-1"></i>URL
                            </>
                          )}
                        </button>
                      ))}
                    </div>

                    {uploadTab === "local" && (
                      <>
                        <div
                          className="dropzone mt-1 border-dashed rounded-4 min-h-0 text-center py-8 px-8 text-muted ls-sm cursor-pointer flex-column"
                          onClick={() =>
                            document.getElementById("evHiddenFileInput").click()
                          }
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={(e) => {
                            e.preventDefault();
                            addFilesWithLimit(Array.from(e.dataTransfer.files));
                          }}
                        >
                          Drag & drop files here, or{" "}
                          <span className="text-danger px-1">click here</span>{" "}
                          to select
                          <small className="d-block text-muted">
                            or choose a folder
                          </small>
                        </div>
                        <div className="d-flex justify-content-center pt-1">
                          <button
                            className="bg-secondary text-white mt-2 rounded-3 border-0 px-5 py-2 fs-6 mb-2"
                            type="button"
                            onClick={() =>
                              document
                                .getElementById("evFolderFileInput")
                                .click()
                            }
                          >
                            <i className="bi bi-folder me-2"></i>Upload from
                            Folder
                          </button>
                        </div>
                        <input
                          type="file"
                          id="evHiddenFileInput"
                          multiple
                          accept="image/*"
                          className="d-none"
                          onChange={(e) =>
                            addFilesWithLimit(Array.from(e.target.files))
                          }
                        />
                        <input
                          type="file"
                          id="evFolderFileInput"
                          webkitdirectory="true"
                          directory=""
                          multiple
                          className="d-none"
                          onChange={(e) =>
                            addFilesWithLimit(
                              Array.from(e.target.files).filter((f) =>
                                f.type.startsWith("image/"),
                              ),
                            )
                          }
                        />
                      </>
                    )}

                    {uploadTab === "drive" && (
                      <div className="border-dashed rounded-4 text-center py-6 px-4">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="52"
                          height="46"
                          viewBox="0 0 87.3 78"
                          className="mb-3"
                        >
                          <path
                            d="M6.6 66.85l3.85 6.65c.8 1.4 1.95 2.5 3.3 3.3L28 48.2H0c0 1.55.4 3.1 1.2 4.5z"
                            fill="#0066da"
                          />
                          <path
                            d="M43.65 24.7L29.35 0c-1.35.8-2.5 1.9-3.3 3.3L1.2 43.7c-.8 1.4-1.2 2.95-1.2 4.5h28z"
                            fill="#00ac47"
                          />
                          <path
                            d="M73.55 76.8c1.35-.8 2.5-1.9 3.3-3.3l1.6-2.75L86.1 57.2c.8-1.4 1.2-2.95 1.2-4.5H59.3l5.9 11.5z"
                            fill="#ea4335"
                          />
                          <path
                            d="M43.65 24.7l14.3-24.7c-1.35-.8-2.9-1.2-4.5-1.2H33.85c-1.6 0-3.15.45-4.5 1.2z"
                            fill="#00832d"
                          />
                          <path
                            d="M59.3 48.2H28L13.75 76.8c1.35.8 2.9 1.2 4.5 1.2h50c1.6 0 3.15-.45 4.5-1.2z"
                            fill="#2684fc"
                          />
                          <path
                            d="M73.4 26.5l-13.1-22.7c-.8-1.4-1.95-2.5-3.3-3.3L43.65 24.7l15.65 23.5h27.9c0-1.55-.4-3.1-1.2-4.5z"
                            fill="#ffba00"
                          />
                        </svg>
                        <p className="text-dark fs-5 mb-0 fw-medium">
                          Import from Google Drive
                        </p>
                        <p className="text-muted fs-6 mb-3 lh-sm">
                          Select images directly from your Google Drive.
                          <br />
                          Only image files will be imported.
                        </p>
                        {!GOOGLE_DRIVE_CONFIGURED ? (
                          <p className="text-danger fs-6 mb-0">
                            Google Drive import is not configured for this
                            environment.
                          </p>
                        ) : (
                          <button
                            className="btn btn-outline-dark rounded-3 fs-6 px-5 py-2"
                            onClick={handleGoogleDriveClick}
                            disabled={driveLoading || !gisReady || !gapiReady}
                          >
                            {driveLoading ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                ></span>
                                Importing...
                              </>
                            ) : !gisReady || !gapiReady ? (
                              <>
                                <span
                                  className="spinner-border spinner-border-sm me-2"
                                  role="status"
                                ></span>
                                Loading Google APIs...
                              </>
                            ) : (
                              <>
                                <i className="bi bi-box-arrow-in-up-right me-2"></i>
                                Open Google Drive Picker
                              </>
                            )}
                          </button>
                        )}
                      </div>
                    )}

                    {uploadTab === "url" && (
                      <div className="border-dashed rounded-4 p-4">
                        <p className="text-dark fs-5 fw-medium mb-0 lh-1">
                          <i className="bi bi-link-45deg me-1"></i> Upload from
                          URL
                        </p>
                        <p className="text-muted fs-6 mb-2">
                          Paste one or more direct image URLs.
                        </p>
                        <textarea
                          className="form-control rounded-3 fs-6 bg-white"
                          rows={5}
                          placeholder={`https://example.com/photo1.jpg\nhttps://example.com/photo2.png`}
                          value={urlInput}
                          onChange={(e) => setUrlInput(e.target.value)}
                          disabled={urlLoading}
                        />
                        <button
                          className="btn btn-dark rounded-3 px-5 py-2 fs-6 fw-normal mt-2"
                          onClick={handleUrlUpload}
                          disabled={urlLoading || !urlInput.trim()}
                        >
                          {urlLoading ? (
                            <>
                              <span
                                className="spinner-border spinner-border-sm me-2"
                                role="status"
                              ></span>
                              Fetching...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-download me-2"></i>Fetch & Add
                              Images
                            </>
                          )}
                        </button>
                        <p className="text-muted mt-2 fs-6 mb-0">
                          Tip: Use direct image URLs ending in .jpg, .png, .webp
                          etc.
                        </p>
                      </div>
                    )}

                    <div className="d-flex flex-column justify-content-center mt-3">
                      {uploading && (
                        <div className="w-100 p-3 rounded-4 bg-danger-soft mb-1">
                          <div className="progress rounded-pill progress-bar-height">
                            <div
                              className="progress-bar progress-bar-striped bg-success"
                              role="progressbar"
                              style={{ width: `${totalProgress}%` }}
                            ></div>
                          </div>
                          <p className="m-0 text-center text-dark lh-1 mt-2">
                            {files.filter((f) => f.status === "done").length}/
                            {files.length} Photos Uploaded ({totalProgress}%)
                          </p>
                        </div>
                      )}
                      <button
                        className="btn btn-dark-primary border-0 px-4 rounded-pill fs-6 fw-medium"
                        onClick={handleUpload}
                        disabled={uploading || files.length === 0}
                      >
                        {uploading ? (
                          <div
                            className="spinner-border spinner-border-sm"
                            role="status"
                          ></div>
                        ) : (
                          <>
                            <i className="bi bi-upload me-1"></i> Upload Now
                          </>
                        )}
                      </button>
                      {uploading && (
                        <div className="text-danger rounded-4 py-2 px-3 text-center">
                          <span className="fst-italic">
                            ⚠️ Upload in progress — don't close this tab.
                          </span>
                        </div>
                      )}
                      {limitError && (
                        <div className="alert alert-danger mt-2 rounded-4 text-center">
                          {limitError}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* RIGHT: File queue */}
                <div className="col-lg-6 col-12 d-flex justify-content-center align-items-center">
                  <div className="w-100">
                    <div
                      className="col-12 h-upload-images px-4 ps-lg-0"
                      ref={scrollContainerRef}
                    >
                      {processingFiles ? (
                        <div className="d-flex flex-column justify-content-center align-items-center h-100">
                          <div
                            className="spinner-border text-dark mb-2"
                            role="status"
                          ></div>
                        </div>
                      ) : files.length > 0 ? (
                        files.map((f, i) => (
                          <div
                            key={i}
                            data-batch={Math.floor(i / 5)}
                            className={`col-12 pt-2 rounded-4 ${f.status === "done" ? "bg-success-soft" : Math.floor(i / 5) === currentBatchRef.current ? "bg-primary-soft" : "bg-light"}`}
                          >
                            <div className="d-flex justify-content-between px-5">
                              <p className="pe-8 text-truncate lh-sm m-0">
                                <span className="text-dark">{f.file.name}</span>
                                <br />
                                {(f.width && f.height) || f.size ? (
                                  <small className="text-secondary">
                                    {f.width &&
                                      f.height &&
                                      `${f.width}×${f.height}px`}
                                    {f.width && f.height && f.size ? " • " : ""}
                                    {f.size}
                                  </small>
                                ) : null}
                              </p>
                              <div className="d-flex align-items-center">
                                <button
                                  disabled={f.status !== "pending"}
                                  className="btn btn-dark rounded-circle delete-button position-relative z-2 p-0"
                                  onClick={() => handleRemove(i)}
                                >
                                  <i className="bi bi-x-lg sm-icon text-white"></i>
                                </button>
                              </div>
                            </div>
                            <div className="mx-3 pt-1">
                              <div
                                className="progress progress-bar-height my-1 w-95"
                                role="progressbar"
                              >
                                <div
                                  className={`progress-bar progress-bar-striped ${f.status === "done" ? "bg-success" : "bg-primary"}`}
                                  style={{
                                    width: `${uploadProgress[i] || 0}%`,
                                    transition: "width 0.3s ease",
                                  }}
                                ></div>
                              </div>
                            </div>
                          </div>
                        ))
                      ) : (
                        <div className="d-flex justify-content-center align-items-center h-100">
                          <img
                            src="/assets/images/images.png"
                            className="w-50"
                            alt=""
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default Event;
